import { useCallback, useState } from 'react';
import { NotificationModel } from '../../mobx/models/notification';
import { NOTIFICATION_TYPE } from '../../global/constants';
import { ButtonDisableState } from '../../interfaces/NotificationInterface';

const isNavigableNotification = (task: NotificationModel): boolean =>
  [
    NOTIFICATION_TYPE.COMMENT,
    NOTIFICATION_TYPE.TASK,
    NOTIFICATION_TYPE.INVITE,
    NOTIFICATION_TYPE.PROJECT_INVITE
  ].includes(task.type) &&
  !!task.projectId &&
  !!task.taskId;

const useNotificationNavigation = (filteredNotifications: NotificationModel[] = []) => {
  const [selectedTask, setSelectedTask] = useState<NotificationModel | null>(null);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [buttonDisable, setButtonDisable] = useState<ButtonDisableState>({ forward: false, backward: false });
  const [isRearranging, setIsRearranging] = useState(false);

  const updateButtonState = useCallback((list: NotificationModel[], item: NotificationModel, taskIndex: number) => {
    const hasForwardTask = list?.slice(taskIndex + 1).some(isNavigableNotification);
    const hasBackwardTask = list?.slice(0, taskIndex).some(isNavigableNotification);
    setButtonDisable({ forward: !hasForwardTask, backward: !hasBackwardTask });
  }, []);

  const handleCloseTaskDetail = useCallback(() => {
    setIsTaskDetailOpen(false);
    setSelectedTask(null);
    setSelectedTaskIndex(null);
  }, []);

  const handleNextNotificationOriginal = useCallback(
    (list?: NotificationModel[]) => {
      if (selectedTaskIndex === null || selectedTaskIndex === undefined) return;
      const notificationList = list || filteredNotifications || [];
      const nextTask = notificationList.slice(selectedTaskIndex + 1).find(isNavigableNotification);
      if (nextTask) {
        const nextTaskIndex = notificationList.findIndex((task) => task?.id === nextTask?.id);
        setSelectedTask(nextTask);
        setSelectedTaskIndex(nextTaskIndex);
        updateButtonState(notificationList, nextTask, nextTaskIndex);
      }
    },
    [filteredNotifications, selectedTaskIndex, updateButtonState]
  );

  const handlePreviousNotificationOriginal = useCallback(
    (list?: NotificationModel[]) => {
      if (selectedTaskIndex === null || selectedTaskIndex === undefined) return;
      const notificationList = list || filteredNotifications || [];
      const previousTask = [...notificationList].slice(0, selectedTaskIndex).reverse().find(isNavigableNotification);
      if (previousTask) {
        const previousTaskIndex = notificationList.findIndex((task) => task?.id === previousTask?.id);
        setSelectedTask(previousTask);
        setSelectedTaskIndex(previousTaskIndex);
        updateButtonState(notificationList, previousTask, previousTaskIndex);
      }
    },
    [filteredNotifications, selectedTaskIndex, updateButtonState]
  );

  const handleNextNotification = useCallback(
    (updatedList = filteredNotifications) => {
      setIsRearranging(true);
      setTimeout(() => {
        handleNextNotificationOriginal(updatedList);
        setTimeout(() => setIsRearranging(false), 50);
      }, 20);
    },
    [handleNextNotificationOriginal, filteredNotifications]
  );

  const handlePreviousNotification = useCallback(
    (updatedList = filteredNotifications) => {
      setIsRearranging(true);
      setTimeout(() => {
        handlePreviousNotificationOriginal(updatedList);
        setTimeout(() => setIsRearranging(false), 50);
      }, 20);
    },
    [handlePreviousNotificationOriginal, filteredNotifications]
  );

  const handleArrowClick = useCallback(
    (type: string) => {
      if (
        selectedTaskIndex !== null &&
        selectedTaskIndex !== undefined &&
        selectedTaskIndex !== filteredNotifications?.length - 1 &&
        type === 'Forward'
      ) {
        handleNextNotification();
      } else if (
        selectedTaskIndex !== null &&
        selectedTaskIndex !== undefined &&
        selectedTaskIndex !== 0 &&
        type === 'Backward'
      ) {
        handlePreviousNotification();
      }
    },
    [handleNextNotification, handlePreviousNotification, filteredNotifications, selectedTaskIndex]
  );

  return {
    selectedTask,
    selectedTaskIndex,
    isTaskDetailOpen,
    buttonDisable,
    isRearranging,
    updateButtonState,
    handleNextNotification,
    handlePreviousNotification,
    handleArrowClick,
    handleCloseTaskDetail,
    setIsTaskDetailOpen,
    setSelectedTask,
    setSelectedTaskIndex
  } as const;
};

export default useNotificationNavigation;
